
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserRound, Phone, Linkedin, Camera } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";

interface Contact {
  id: string;
  full_name: string | null;
  title: string | null;
  work_email: string | null;
  linkedin_profile?: string | null;
  phone_number?: string | null;
  key_contact: boolean;
  avatar_url?: string | null;
}

interface ContactCardProps {
  contact: Contact;
  onPhotoClick: () => void;
}

export function ContactCard({ contact, onPhotoClick }: ContactCardProps) {
  const { hasRole } = useUserRole();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const checkRole = async () => {
      const hasAccess = await hasRole('sys_admin');
      setCanEdit(hasAccess);
    };
    checkRole();
  }, [hasRole]);

  const imageUrl = contact.avatar_url;

  return (
    <div className="flex items-start space-x-3 p-4 rounded-lg border bg-background min-w-0">
      <div className="flex-shrink-0">
        <div 
          className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center relative group ${
            canEdit ? 'cursor-pointer hover:opacity-90' : ''
          }`}
          onClick={() => canEdit && onPhotoClick()}
        >
          <Avatar className="h-10 w-10 rounded-lg">
            <AvatarImage src={imageUrl} alt={contact.full_name || ""} />
            <AvatarFallback>
              <UserRound className="h-6 w-6 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          {canEdit && (
            <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="font-medium break-words">{contact.full_name || 'N/A'}</p>
        {contact.title && (
          <p className="text-sm text-muted-foreground break-words">{contact.title}</p>
        )}
        <div className="flex flex-col gap-1">
          {contact.work_email && (
            <a 
              href={`mailto:${contact.work_email}`}
              className="text-sm text-blue-600 hover:underline break-all"
            >
              {contact.work_email}
            </a>
          )}
          {contact.phone_number && (
            <a 
              href={`tel:${contact.phone_number}`}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <Phone className="h-3 w-3 flex-shrink-0" />
              <span className="break-all">{contact.phone_number}</span>
            </a>
          )}
          {contact.linkedin_profile && (
            <a 
              href={contact.linkedin_profile}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <Linkedin className="h-3 w-3 flex-shrink-0" />
              <span className="break-all">LinkedIn Profile</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
